import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Use translations for the ReviewsCarousel component
export const ReviewsCarousel = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for right to left, -1 for left to right

  // Create testimonial objects using i18n
  const testimonials = [
    {
      testimony: t(
        "testimonies.cassandre_w.testimony",
        "Photographe.ai is a gem. I created stylish, lifelike portraits in minutes. Each session feels fresh – perfect for creative expression or just for fun."
      ),
      name: t("testimonies.cassandre_w.name", "Cassandre W."),
      imageBefore: "/images/testimonies/testimony_cassandre_before.jpg",
      imageAfter: "/images/testimonies/testimony_cassandre.jpg",
    },
    {
      testimony: t(
        "testimonies.genia_h.testimony",
        "This tool makes me feel like I have my own design team. The interface is smooth and the results are more than impressive. Great for profiles, content, or just playing around."
      ),
      name: t("testimonies.genia_h.name", "Genia H."),
      imageBefore: "/images/testimonies/testimony_genia_before.jpg",
      imageAfter: "/images/testimonies/testimony_genia.jpg",
    },
    {
      testimony: t(
        "testimonies.cyril_g.testimony",
        "I didn't expect much... but wow. The images are breathtaking. It captured my face in so many unique styles. It feels like magic, but with real utility behind it."
      ),
      name: t("testimonies.cyril_g.name", "Cyril G."),
      imageBefore: "/images/testimonies/testimony_cyril_before.jpg",
      imageAfter: "/images/testimonies/testimony_cyril.webp",
    },
    {
      testimony: t(
        "testimonies.claude_b.testimony",
        "This is next-level AI. I used it for work photos and ended up creating a sci-fi poster of myself for fun. It's simple, fast, and surprisingly creative."
      ),
      name: t("testimonies.claude_b.name", "Claude B."),
      imageBefore: "/images/testimonies/testimony_claude_before.jpg",
      imageAfter: "/images/testimonies/testimony_claude.jpg",
    },
    {
      testimony: t(
        "testimonies.thibault.testimony",
        "Photographe is incredible! The AI works wonders on photos and the interface is super easy to use. The filters are great and sharing your creations is a breeze. In short, it's the perfect app for all photo enthusiasts!"
      ),
      name: t("testimonies.thibault.name", "Thibault T."),
      imageBefore: "/images/testimonies/testimony_thibault_before.jpg",
      imageAfter: "/images/testimonies/testimony_thibault.jpg",
    },
    {
      testimony: t(
        "testimonies.romaric_mollard.testimony",
        "Photographe.ai is great, affordable, and super easy to use. After setting up your model, generating images takes only a few seconds. Not all will be perfect, but you'll definitely find what you're looking for."
      ),
      name: t("testimonies.romaric_mollard.name", "Romaric M."),
      imageBefore: "/images/testimonies/testimony_romaric_before.jpg",
      imageAfter: "/images/testimonies/testimony_romaric.webp",
    },
    {
      testimony: t(
        "testimonies.ilya.testimony",
        "Great app! Much cheaper and faster than hiring a photographer, and the results are excellent. I love being able to customize the photo style to my preferences."
      ),
      name: t("testimonies.ilya.name", "Ilya U."),
      imageBefore: "/images/testimonies/testimony_ilya_before.jpg",
      imageAfter: "/images/testimonies/testimony_ilya.jpg",
    },
    {
      testimony: t(
        "testimonies.mathieu.testimony",
        "PhotographeAI offers incredible results. The generated photos are professional, natural, and of great quality. An excellent application that I recommend without hesitation!"
      ),
      name: t("testimonies.mathieu.name", "Mathieu D."),
      imageBefore: "/images/testimonies/testimony_mathieu_before.jpg",
      imageAfter: "/images/testimonies/testimony_mathieu.jpg",
    },
    {
      testimony: t(
        "testimonies.gregoire.testimony",
        "Incredible AI photo generator! I've created so many beautiful and realistic images with just a few clicks. The variety of styles is impressive!"
      ),
      name: t("testimonies.gregoire.name", "Grégoire P."),
      imageBefore: "/images/testimonies/testimony_gregoire_before.jpg",
      imageAfter: "/images/testimonies/testimony_gregoire.webp",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPreviousIndex(currentIndex);
      const newIndex = (currentIndex + 1) % testimonials.length;
      setDirection(1); // Forward direction
      setCurrentIndex(newIndex);
    }, 7000); // Longer interval for better readability
    return () => clearInterval(interval);
  }, [testimonials.length, currentIndex]);

  const handleDotClick = (index: number) => {
    setPreviousIndex(currentIndex);
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  return (
    <div className="h-full overflow-hidden relative flex flex-col justify-center">
      <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2 z-10">
        {testimonials.map((_, index) => (
          <button
            key={`dot-${index}`}
            onClick={() => handleDotClick(index)}
            className={`h-2 w-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? "bg-white w-4" : "bg-white/30"
            }`}
            aria-label={`View review ${index + 1}`}
          ></button>
        ))}
      </div>

      {testimonials.map((review, index) => {
        const hasBeforeAfter =
          review.imageAfter && review.imageAfter !== review.imageBefore;
        const isActive = index === currentIndex;
        const isLeaving = index === previousIndex && index !== currentIndex;

        return (
          <motion.div
            key={index}
            className={`w-full max-w-3xl mx-auto h-full flex flex-col justify-center absolute inset-0 px-4`}
            initial={{ opacity: 0, x: 100 }}
            animate={{
              opacity: isActive ? 1 : isLeaving ? 0 : 0,
              x: isActive
                ? 0
                : isLeaving
                ? direction > 0
                  ? -100
                  : 100
                : direction > 0
                ? 100
                : -100,
            }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white dark:bg-black/40 bg-white backdrop-blur-sm p-6 rounded-xl border border-white/20 dark:border-white/20 border-gray-200 shadow-xl">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Images section */}
                <div
                  className={`flex ${
                    hasBeforeAfter
                      ? "flex-row w-full md:w-1/2"
                      : "w-full md:w-1/4"
                  } shrink-0`}
                >
                  {/* Before Image - Slightly smaller */}
                  <div
                    className={`relative ${
                      hasBeforeAfter
                        ? "h-44 w-1/2 md:h-52 scale-90"
                        : "h-44 w-full md:h-52 scale-90"
                    }`}
                  >
                    <div className="relative h-full w-full">
                      <img
                        src={review.imageBefore}
                        alt={`${review.name} before`}
                        className="object-cover h-full w-full border-2 border-white/70 dark:border-white/70 border-gray-300 rounded-md contrast-75 brightness-110"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/300x400?text=Before";
                        }}
                      />
                      {hasBeforeAfter && (
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {t("reviews.before", "Before")}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* After Image */}
                  {hasBeforeAfter && (
                    <div className="relative h-44 w-1/2 md:h-52 -ml-4">
                      <div className="relative h-full w-full">
                        <img
                          src={review.imageAfter}
                          alt={`${review.name} after`}
                          className="object-cover h-full w-full rounded-md border-2 border-white/70 dark:border-white/70 border-gray-300 shadow-lg"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "https://via.placeholder.com/300x400?text=AI+Generated";
                          }}
                        />
                        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {t("reviews.ai_generated", "AI Generated")}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="w-full md:w-1/2 flex flex-col justify-between">
                  <div className="flex mb-3">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 text-yellow-400`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 15.934l-6.195 3.763 1.647-7.05L.306 7.447l7.128-.611L10 .342l2.566 6.494 7.128.611-5.146 5.2 1.647 7.05L10 15.934z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ))}
                  </div>

                  <blockquote className="dark:text-white text-gray-800 text-base md:text-base mb-4 italic leading-relaxed">
                    "{review.testimony}"
                  </blockquote>

                  <div className="flex items-center">
                    <p className="dark:text-white text-gray-800 font-semibold">
                      {review.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};
